import React, { FunctionComponent, useState, memo, useRef } from "react"
import { MarkerClusterer, InfoWindow } from "@react-google-maps/api"
import { findIndex } from "lodash"

import markerCluster from "assets/img/map-marker-cluster.svg"

import MapMarker, { IMapMarkerProps } from "components/MapView/MapMarker/MapMarker"
import MapMarkersPopover from "components/MapView/MapMarkersPopover/MapMarkersPopover"
import useClickOutside from "hooks/useClickOutside"
import { MapType } from "config/constants"
import LocationService from "services/LocationService"

interface IMapClustererProps {
    markers?: IMapMarkerProps[]
    mapType: MapType
    updateClusterPopover(el?: React.JSX.Element): void
    preventInfoWindowsClose?: boolean
}

const MapClusterer: FunctionComponent<React.PropsWithChildren<IMapClustererProps>> = ({
    markers = [],
    mapType,
    updateClusterPopover,
    preventInfoWindowsClose
}) => {
    const [visibleMarkerIndexes, setVisibleMarkerIndexes] = useState<number[]>([])
    const clusterPopoverEl = useRef<InfoWindow>(null)

    useClickOutside(() => {
        if (!preventInfoWindowsClose) {
            updateClusterPopover(undefined)
        }
    }, clusterPopoverEl.current?.containerElement)

    function onClusteringEnd(clusterer) {
        calcVisibleMarkers(clusterer)
    }

    function calcVisibleMarkers(clusterer) {
        let newVisibleMarkerIndexes: number[] = []
        for (let cluster of clusterer.clusters) {
            const clusterMarkers = cluster.getMarkers()
            if (clusterMarkers.length < cluster.minClusterSize) {
                newVisibleMarkerIndexes = [...newVisibleMarkerIndexes, ...clusterMarkers.map(marker => marker.zIndex)]
            }
        }

        setVisibleMarkerIndexes(newVisibleMarkerIndexes)
    }

    const isMarkerWithIndexInCluster = (markerIndex?: number) => {
        if (typeof markerIndex === "undefined") return false
        return !visibleMarkerIndexes.includes(markerIndex)
    }

    function collectSourceMarkersFromCluster(cluster): IMapMarkerProps[] {
        const clusterMarkers = cluster.getMarkers()
        const mapMarkers: any[] = cluster.markerClusterer.getMarkers()
        const collectedMarkers: IMapMarkerProps[] = []
        for (let clusterMarker of clusterMarkers) {
            const currentIndex = findIndex(mapMarkers, mapMarker => mapMarker.zIndex === clusterMarker.zIndex)
            if (currentIndex > -1) {
                collectedMarkers.push(markers[currentIndex])
            }
        }

        return collectedMarkers
    }

    function showClusterPopover(cluster) {
        const markersToRender = collectSourceMarkersFromCluster(cluster)
        updateClusterPopover(
            <InfoWindow
                ref={clusterPopoverEl}
                position={cluster.getCenter()}
                zIndex={markers.length + 1}
                options={{
                    pixelOffset: new google.maps.Size(0, -15)
                }}>
                <MapMarkersPopover markers={markersToRender} />
            </InfoWindow>
        )
    }

    async function onClusterClick(cluster) {
        const map = cluster.getMap()
        try {
            const isMaxZoom = await LocationService.checkIfIsMaxZoomAtPointForMapType(map.getZoom(), cluster.getCenter(), mapType)

            if (isMaxZoom) {
                showClusterPopover(cluster)
                return
            }
        } catch(e) {}

        map.fitBounds(cluster.getBounds())
    }

    return (
        <MarkerClusterer
            onClusteringEnd={onClusteringEnd}
            zoomOnClick={false}
            onClick={onClusterClick}
            averageCenter
            options={{
                calculator: (clusterMarkers) => ({
                    title: clusterMarkers.length.toString(),
                    text: clusterMarkers.length.toString(),
                    index: 1
                }),
                styles: [{
                    url: markerCluster,
                    width: 44,
                    height: 44,
                    textColor: "#fff",
                    textSize: 12,
                    fontFamily: "Roboto"
                }]
            }}>
                {clusterer => {
                    return <>{markers && markers.map((marker, index) => (
                        <MapMarker
                            {...marker}
                            key={index}
                            index={index + 1}
                            isInCluster={isMarkerWithIndexInCluster(marker.index)}
                            clusterer={clusterer} />
                    ))}</>
                }}
        </MarkerClusterer>
    )
}

export default memo(MapClusterer)
